/**
 * Reactions sent from iPhones arrive as plain SMS text, e.g. Loved “See you soon” or
 * Liked an image. This is a port of Android's Reactions.kt / ReactionsParser.kt: it finds
 * those messages and attaches them to the message they react to, so the thread can show
 * them as reactions instead of plain text.
 */
import { isPremium } from '@/utils/premium.js';

export const REACTIONS = [
    { id: 'liked', keyword: 'Liked', emoji: '👍' },
    { id: 'loved', keyword: 'Loved', emoji: '❤️' },
    { id: 'disliked', keyword: 'Disliked', emoji: '👎' },
    { id: 'laughed', keyword: 'Laughed at', emoji: '😂' },
    { id: 'emphasized', keyword: 'Emphasized', emoji: '‼️' },
    { id: 'questioned', keyword: 'Questioned', emoji: '❓' }
];

const TEXT_SUFFIX = '”';
const IMAGE_SUFFIX = 'an image';
const VIDEO_SUFFIX = 'a video';

// iOS caps the quoted text at ~40 characters and appends an ellipsis.
const TRUNCATED_MIN_LENGTH = 40;
const ELLIPSIS = '…';

export function reactionsEnabled (subscriptionType) {
    return isPremium(subscriptionType);
}

function cleanup (text) {
    return text.replace(/\r\n/g, '').replace(/\n/g, '').replace(/\r/g, '');
}

function textOf (message) {
    return typeof message.dataNoEmoji === 'string' ? message.dataNoEmoji : null;
}

function isText (message) {
    return typeof message.mime_type === 'string' && message.mime_type.indexOf('text') === 0;
}

// iOS sends exactly `<Keyword> “<quoted text>”`, `<Keyword> an image` or `<Keyword> a video`.
function shapeOf (reaction, data) {
    if (data === reaction.keyword + ' ' + IMAGE_SUFFIX) {
        return IMAGE_SUFFIX;
    }

    if (data === reaction.keyword + ' ' + VIDEO_SUFFIX) {
        return VIDEO_SUFFIX;
    }

    const textPrefix = reaction.keyword + ' “';
    if (data.startsWith(textPrefix) && data.endsWith(TEXT_SUFFIX) &&
            data.length > textPrefix.length + TEXT_SUFFIX.length) {
        return cleanup(data.substring(textPrefix.length, data.length - TEXT_SUFFIX.length).trim());
    }

    return null;
}

/**
 * Reactions.kt findReaction() and ReactionsParser.kt reactionContent(): the reaction type and
 * what it points at (the quoted text, or the image/video suffix), or null if the message isn't
 * a reaction. Stricter than Android, which only checks the keyword and the ending, so ordinary
 * texts like Loved the book “Dune” or a quote containing "an image" aren't misread.
 */
function parseReaction (message) {
    if (message.marker || !isText(message)) {
        return null;
    }

    const data = textOf(message);
    if (data === null) {
        return null;
    }

    for (const reaction of REACTIONS) {
        const content = shapeOf(reaction, data);
        if (content !== null) {
            return { reaction, content };
        }
    }

    return null;
}

/** The reaction type, or null if the message isn't one. */
export function findReaction (message) {
    const parsed = parseReaction(message);
    return parsed === null ? null : parsed.reaction;
}

function matches (reaction, message) {
    const content = reaction.content;
    if (content === null) {
        return false;
    }

    const data = textOf(message);
    if (data !== null && isText(message)) {
        if (cleanup(data) === content) {
            return true;
        }

        if (content.length >= TRUNCATED_MIN_LENGTH && content.endsWith(ELLIPSIS) &&
                cleanup(data).startsWith(content.substring(0, content.length - 1))) {
            return true;
        }
    }

    const mimeType = message.mime_type || '';
    if (content === IMAGE_SUFFIX && mimeType.indexOf('image/') === 0) {
        return true;
    }

    return content === VIDEO_SUFFIX && mimeType.indexOf('video/') === 0;
}

/**
 * ReactionsParser.kt parseReactions(), for a thread's messages in chronological order.
 *
 * Returns { hidden, reactions }: `hidden` holds the device_ids of reaction messages that were
 * attached to a target, and `reactions` maps a target's device_id to its reactions (oldest
 * first). Like Android, each reaction goes to the most recent earlier message with matching
 * content. Unlike Android, a reaction whose target isn't loaded (the web pages through older
 * messages) stays visible as plain text instead of disappearing.
 */
export function parseReactions (messages) {
    const result = { hidden: {}, reactions: {} };

    const found = [];
    for (const message of messages) {
        const parsed = parseReaction(message);
        if (parsed !== null) {
            found.push({ reaction: parsed.reaction, content: parsed.content, original: message });
        }
    }

    if (found.length === 0) {
        return result;
    }

    const isReactionMessage = {};
    for (const item of found) {
        isReactionMessage[item.original.device_id] = true;
    }
    const candidates = messages.filter(message => !message.marker && !isReactionMessage[message.device_id]);

    for (const item of found) {
        for (let i = candidates.length - 1; i >= 0; i--) {
            const message = candidates[i];
            if (item.original.timestamp < message.timestamp) {
                continue;
            }

            if (matches(item, message)) {
                const targetId = message.device_id;
                (result.reactions[targetId] = result.reactions[targetId] || []).push(item.reaction);
                result.hidden[item.original.device_id] = true;
                break;
            }
        }
    }

    return result;
}
